import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, ShieldIcon, UsersIcon, ArrowLeftIcon, CheckCircle2Icon } from 'lucide-react';
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function DeepAssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = Intro, 1-5 = Assessment Sections
  const [direction, setDirection] = useState(1);
  // State for selections
  const [devices, setDevices] = useState<string[]>([]);
  const [accounts, setAccounts] = useState({
    pm: '',
    bank: '',
    email: '',
    social: ''
  });
  const [habits, setHabits] = useState({
    scamConfidence: 3,
    updates: ''
  });
  const [household, setHousehold] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const goToStep = (newStep: number) => {
    setDirection(newStep > step ? 1 : -1);
    setStep(newStep);
  };
  const handleComplete = () => {
    navigate('/assessment-score');
  };
  const toggleArrayItem = (array: string[], setArray: (val: string[]) => void, item: string) => {
    if (array.includes(item)) {
      setArray(array.filter(i => i !== item));
    } else {
      setArray([...array, item]);
    }
  };
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col overflow-hidden font-sans" data-id="element-1180">
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-1181">
        {step === 0 && <motion.div key="intro" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="absolute inset-0 w-full min-h-screen bg-[#1a0647] flex flex-col items-center justify-center px-5 py-6" data-id="element-1182">
            <div className="w-full max-w-md mx-auto flex flex-col items-center text-center flex-1" data-id="element-1183">
              <img src="https://cdn.magicpatterns.com/uploads/ddFjEWvaDfXw4mBqdFC5Gd/Caveo_Logotype_Horizontal_240x50_White_PNG.png" alt="Caveo" className="h-6 mb-8 opacity-80 mt-4" data-id="element-1184" />

              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight" data-id="element-1185">
                Let's map out what your household actually needs.
              </h1>
              <p className="text-[#d0bdf5] text-base mb-8 max-w-sm" data-id="element-1186">
                Takes about 10 minutes. We'll look at your devices, accounts,
                and habits to build a personalized action plan.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 w-full" data-id="element-1187">
                <div className="flex flex-col items-center gap-1.5" data-id="element-1188">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1189">
                    <ClockIcon size={20} className="text-[#0EB4CC]" data-id="element-1190" />
                  </div>
                  <span className="text-white text-xs font-medium" data-id="element-1191">
                    10 minutes
                  </span>
                </div>
                <div className="hidden sm:block w-px h-8 bg-white/20" data-id="element-1192"></div>
                <div className="flex flex-col items-center gap-1.5" data-id="element-1193">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1194">
                    <ShieldIcon size={20} className="text-[#0EB4CC]" data-id="element-1195" />
                  </div>
                  <span className="text-white text-xs font-medium" data-id="element-1196">
                    Personalized action plan
                  </span>
                </div>
                <div className="hidden sm:block w-px h-8 bg-white/20" data-id="element-1197"></div>
                <div className="flex flex-col items-center gap-1.5" data-id="element-1198">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1199">
                    <UsersIcon size={20} className="text-[#0EB4CC]" data-id="element-1200" />
                  </div>
                  <span className="text-white text-xs font-medium" data-id="element-1201">
                    Built for your household
                  </span>
                </div>
              </div>

              <div className="w-full space-y-3 mt-auto" data-id="element-1202">
                <button onClick={() => goToStep(1)} className="w-full bg-[#8528e2] text-white font-bold py-3.5 rounded-xl hover:bg-[#7322c4] transition-colors shadow-lg text-base" data-id="element-1203">
                  Start Assessment
                </button>
                <button onClick={() => navigate('/home')} className="w-full bg-transparent text-white/60 font-semibold py-3.5 rounded-xl hover:bg-white/5 transition-colors text-base" data-id="element-1204">
                  Remind Me Later
                </button>
              </div>

              <p className="text-white/40 text-[10px] mt-4" data-id="element-1205">
                Your answers are private by default. You control what is shared
                with your family.
              </p>
            </div>
          </motion.div>}

        {step > 0 && <motion.div key={`step${step}`} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="absolute inset-0 w-full min-h-screen bg-[#f8f8f8] flex flex-col" data-id="element-1206">
            {/* Header & Progress */}
            <div className="bg-white border-b border-gray-200 pt-6 pb-3 px-5 sticky top-0 z-10" data-id="element-1207">
              <div className="max-w-md mx-auto" data-id="element-1208">
                <div className="flex items-center justify-between mb-3" data-id="element-1209">
                  <button onClick={() => goToStep(step - 1)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-1210">
                    <ArrowLeftIcon size={18} data-id="element-1211" />
                  </button>
                  <div className="text-xs font-bold text-[#1a0647] uppercase tracking-wider" data-id="element-1212">
                    {step === 1 && 'Section 1: Your Devices'}
                    {step === 2 && 'Section 2: Your Accounts'}
                    {step === 3 && 'Section 3: Your Habits'}
                    {step === 4 && 'Section 4: Your Household'}
                    {step === 5 && 'Section 5: Your Goals'}
                    <span className="text-gray-400 ml-1.5 font-medium normal-case" data-id="element-1213">
                      {step} of 5
                    </span>
                  </div>
                  <div className="w-8" data-id="element-1214"></div> {/* Spacer for centering */}
                </div>
                {/* Progress Bar */}
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-1215">
                  <div className="h-full bg-[#0EB4CC] transition-all duration-500 ease-out" style={{
                width: `${step / 5 * 100}%`
              }} data-id="element-1216"></div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4 hide-scrollbar" data-id="element-1217">
              <div className="max-w-md mx-auto h-full flex flex-col" data-id="element-1218">
                {step === 1 && <div className="space-y-4" data-id="element-1219">
                    <h2 className="text-xl font-bold text-[#1a0647] mb-4" data-id="element-1220">
                      What types of devices do you have in your home?
                    </h2>
                    <div className="flex flex-wrap gap-2.5" data-id="element-1221">
                      {['iPhone', 'Android', 'Mac', 'Windows PC', 'Chromebook', 'iPad', 'Smart TV', 'Smart Speaker', 'Router'].map(device => <button key={device} onClick={() => toggleArrayItem(devices, setDevices, device)} className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${devices.includes(device) ? 'bg-[#8528e2] text-white border-[#8528e2] shadow-md' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1222">
                          {device}
                        </button>)}
                    </div>
                  </div>}

                {step === 2 && <div className="space-y-6" data-id="element-1223">
                    <div data-id="element-1224">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-3" data-id="element-1225">
                        Do you use a password manager?
                      </h2>
                      <div className="flex gap-2.5" data-id="element-1226">
                        {['Yes', 'No', 'Not sure'].map(opt => <button key={opt} onClick={() => setAccounts({
                    ...accounts,
                    pm: opt
                  })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all border ${accounts.pm === opt ? 'bg-[#8528e2] text-white border-[#8528e2] shadow-md' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1227">
                            {opt}
                          </button>)}
                      </div>
                    </div>

                    <div data-id="element-1228">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-3" data-id="element-1229">
                        Is Two-Factor Authentication (2FA) enabled on:
                      </h2>
                      <div className="space-y-3" data-id="element-1230">
                        {[{
                    key: 'bank',
                    label: 'Your bank account?'
                  }, {
                    key: 'email',
                    label: 'Your primary email?'
                  }, {
                    key: 'social',
                    label: 'Your social media?'
                  }].map(item => <div key={item.key} className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm" data-id="element-1231">
                            <p className="font-medium text-[#111111] text-sm mb-2.5" data-id="element-1232">
                              {item.label}
                            </p>
                            <div className="flex gap-2" data-id="element-1233">
                              {['Yes', 'No'].map(opt => <button key={opt} onClick={() => setAccounts({
                        ...accounts,
                        [item.key]: opt
                      })} className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all border ${accounts[item.key as keyof typeof accounts] === opt ? 'bg-[#8528e2] text-white border-[#8528e2]' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-1234">
                                  {opt}
                                </button>)}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>}

                {step === 3 && <div className="space-y-8" data-id="element-1235">
                    <div data-id="element-1236">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-4" data-id="element-1237">
                        How confident are you in identifying a scam email or
                        text?
                      </h2>
                      <div className="px-2" data-id="element-1238">
                        <input type="range" min="1" max="5" value={habits.scamConfidence} onChange={e => setHabits({
                    ...habits,
                    scamConfidence: parseInt(e.target.value)
                  })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#8528e2]" data-id="element-1239" />
                        <div className="flex justify-between mt-3 text-xl" data-id="element-1240">
                          <span data-id="element-1241">😵‍💫</span>
                          <span data-id="element-1242">🤔</span>
                          <span data-id="element-1243">😐</span>
                          <span data-id="element-1244">🙂</span>
                          <span data-id="element-1245">😎</span>
                        </div>
                      </div>
                    </div>

                    <div data-id="element-1246">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-3" data-id="element-1247">
                        How often do you update your devices?
                      </h2>
                      <div className="space-y-2.5" data-id="element-1248">
                        {['Automatically', 'When I remember', 'Rarely', 'Never'].map(opt => <button key={opt} onClick={() => setHabits({
                    ...habits,
                    updates: opt
                  })} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border ${habits.updates === opt ? 'bg-[#f0ebf8] text-[#8528e2] border-[#8528e2] shadow-sm' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1249">
                            {opt}
                          </button>)}
                      </div>
                    </div>
                  </div>}

                {step === 4 && <div className="space-y-4" data-id="element-1250">
                    <h2 className="text-xl font-bold text-[#1a0647] mb-1" data-id="element-1251">
                      Who might be at higher risk in your household?
                    </h2>
                    <p className="text-gray-500 text-sm mb-4" data-id="element-1252">
                      Select all that apply.
                    </p>
                    <div className="space-y-2.5" data-id="element-1253">
                      {['Child under 13', 'Teenager', 'Older adult over 65', 'Someone with memory concerns', 'No one'].map(person => <button key={person} onClick={() => {
                  if (person === 'No one') {
                    setHousehold(['No one']);
                  } else {
                    const newArr = household.filter(h => h !== 'No one');
                    toggleArrayItem(newArr, setHousehold, person);
                  }
                }} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border flex items-center justify-between ${household.includes(person) ? 'bg-[#f0ebf8] text-[#8528e2] border-[#8528e2] shadow-sm' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1254">
                          {person}
                          {household.includes(person) && <div className="w-4 h-4 rounded-full bg-[#8528e2] flex items-center justify-center" data-id="element-1255">
                              <CheckCircle2Icon size={12} className="text-white" data-id="element-1256" />
                            </div>}
                        </button>)}
                    </div>
                  </div>}

                {step === 5 && <div className="space-y-4" data-id="element-1257">
                    <h2 className="text-xl font-bold text-[#1a0647] mb-1" data-id="element-1258">
                      What would success look like for you?
                    </h2>
                    <p className="text-gray-500 text-sm mb-4" data-id="element-1259">
                      Select all that apply.
                    </p>
                    <div className="space-y-2.5" data-id="element-1260">
                      {['Stop worrying about getting hacked', 'Protect my parents from scams', 'Set up my kids safely online', 'Save money on tech subscriptions'].map(goal => <button key={goal} onClick={() => toggleArrayItem(goals, setGoals, goal)} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border flex items-center justify-between ${goals.includes(goal) ? 'bg-[#f0ebf8] text-[#8528e2] border-[#8528e2] shadow-sm' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1261">
                          {goal}
                          {goals.includes(goal) && <div className="w-4 h-4 rounded-full bg-[#8528e2] flex items-center justify-center" data-id="element-1262">
                              <CheckCircle2Icon size={12} className="text-white" data-id="element-1263" />
                            </div>}
                        </button>)}
                    </div>
                  </div>}

                {/* Footer CTA */}
                <div className="mt-auto pt-6 pb-4" data-id="element-1264">
                  <button onClick={() => step === 5 ? handleComplete() : goToStep(step + 1)} className="w-full bg-[#8528e2] text-white font-bold py-3.5 rounded-xl hover:bg-[#7322c4] transition-colors shadow-md text-base" data-id="element-1265">
                    {step === 5 ? 'See My Action Plan' : 'Continue'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}